import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useToast } from '@/contexts/admin/ToastContext';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { useToggleSavedItemMutation } from '@/store/api';
import { 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  SellerChatModal, 
  PhotoGalleryModal,
  ImageCarousel,
} from '@/components/residents/marketplace';
import {
  SellerCard,
  ItemDetailsSection,
  ActionButtonRow,
} from '@/components/residents/marketplace/shared';
import { 
  MARKETPLACE_MAX_CONTENT_WIDTH,
  getResponsivePadding,
  calculateCarouselDimensions,
} from '@/utils/marketplace';

type SavedItemDetailNavigationProp = StackNavigationProp<RootStackParamList, 'SavedItemDetail'>;
type SavedItemDetailRouteProp = RouteProp<RootStackParamList, 'SavedItemDetail'>;

export const SavedItemDetailScreen: React.FC = () => {
  const navigation = useNavigation<SavedItemDetailNavigationProp>();
  const route = useRoute<SavedItemDetailRouteProp>();
  const { layoutMode, width: screenWidth, isWeb } = useResponsive();
  const { showSuccess } = useToast();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  // Calculate carousel dimensions based on layout
  const carouselDimensions = calculateCarouselDimensions(layoutMode, screenWidth);
  
  const { listing } = route.params;
  const [isBookmarked, setIsBookmarked] = useState(true); // Always starts as bookmarked since it's a saved item
  const [isChatModalVisible, setIsChatModalVisible] = useState(false);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);

  // API mutations
  const [toggleSavedItem] = useToggleSavedItemMutation();

  const isSold = listing.isSold;

  const handleGoBack = () => {
    // If item was unsaved, pass the unsavedId so it gets removed from the list
    if (!isBookmarked) {
      navigation.navigate('SavedListings', { unsavedId: listing.id } as any);
    } else {
      navigation.goBack();
    }
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleBookmarkPress = async () => {
    const newBookmarkState = !isBookmarked;
    setIsBookmarked(newBookmarkState);
    
    try {
      await toggleSavedItem(listing.id).unwrap();
      if (newBookmarkState) {
        showSuccess('Added to your saved items!');
      } else {
        showSuccess('Removed from your saved items!');
      }
    } catch (error) {
      // Revert on error
      setIsBookmarked(!newBookmarkState);
      console.error('Failed to toggle saved item:', error);
    }
  };

  const handleContactSeller = () => {
    setIsChatModalVisible(true);
  };

  const handleCloseChatModal = () => {
    setIsChatModalVisible(false);
  };

  const handleReportListing = () => {
    // TODO: Open report modal
    console.log('Report listing pressed for:', listing.id);
  };

  const handleImagePress = (index: number) => {
    setGalleryInitialIndex(index);
    setIsGalleryVisible(true);
  };

  const handleCloseGallery = () => {
    setIsGalleryVisible(false);
  };

  const handleGalleryContactSeller = () => {
    setIsGalleryVisible(false);
    // Small delay to let the gallery close animation finish
    setTimeout(() => {
      setIsChatModalVisible(true);
    }, 100);
  };

  const handleSellerPress = () => {
    navigation.navigate('SellerProfile', { sellerId: listing.seller.id });
  };

  return (
    <>
      <MarketplaceScreenLayout navigatorTitle="Item details">
        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={[
            styles.content,
            { padding: contentPadding },
            !isMobile && styles.contentLarge,
          ]}
          showsVerticalScrollIndicator={false}
        >
          {/* Responsive content wrapper */}
          <View style={[
            styles.contentWrapper,
            maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
          ]}>
            {/* Back Navigation */}
            <BreadcrumbNavigation 
              onHomePress={handleGoToMarketplace}
              onBackPress={handleGoBack}
            />

            {/* Image Carousel - centered on desktop */}
            <View style={!isMobile && styles.carouselContainer}>
              <ImageCarousel
                images={listing.images.map(img => img.url)}
                onImagePress={handleImagePress}
                showBookmark={true}
                isBookmarked={isBookmarked}
                onBookmarkPress={handleBookmarkPress}
                width={!isMobile ? carouselDimensions.width : undefined}
                height={!isMobile ? carouselDimensions.height : undefined}
                layoutMode={layoutMode}
                showArrows={!isMobile}
              />
            </View>

            {/* Details Section */}
            <ItemDetailsSection
              price={listing.price}
              title={listing.title}
              description={listing.description}
              condition={listing.condition}
              pickupOption={listing.pickupOption}
              isSold={isSold}
            />

            {/* Seller Card */}
            <SellerCard
              seller={listing.seller}
              onPress={handleSellerPress}
            />

            {/* Action Buttons - Only show for non-sold items */}
            {!isSold && (
              <ActionButtonRow
                onContactPress={handleContactSeller}
                onReportPress={handleReportListing}
              />
            )}
          </View>
        </ScrollView>
      </MarketplaceScreenLayout>

      {/* Modals - rendered outside the layout */}
      <SellerChatModal
        visible={isChatModalVisible}
        onClose={handleCloseChatModal}
        listing={listing}
      />
      <PhotoGalleryModal
        visible={isGalleryVisible}
        onClose={handleCloseGallery}
        images={listing.images.map(img => img.url)}
        initialIndex={galleryInitialIndex}
        onContactSeller={isSold ? () => {} : handleGalleryContactSeller}
        onReport={isSold ? () => {} : handleReportListing}
      />
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 20,
  },
  contentLarge: {
    gap: 28,
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 20,
  },
  carouselContainer: {
    alignItems: 'center',
  },
});
